import { LitElement, html, unsafeCSS, TemplateResult, PropertyValues } from 'lit';
import { property, customElement, state } from 'lit/decorators.js';
import { TimelineData, TimelineEvent, TimelineGroup } from '../types';
import CollapsiblePanelController from '../../reactiveControllers/CollapsiblePanelController';
import { GzCollapsiblePanelWebComponent } from '../../gzCollapsiblePanel';
import { styles as componentStyles } from '../view/timelineStyles.scss';
import { repeat } from 'lit/directives/repeat.js';

/**
 * @summary This component represents a collapsible list of timeline items, which can be grouped or ungrouped * @property name - The name of the card group
 * @property {TimelineData[]} data - The array of grouped or ungrouped timeline items
 * @tag gz-collapsible-timeline
 */
@customElement('gz-collapsible-timeline')
export class GzCollapsibleTimelineWebComponent extends LitElement {
  @property({ type: Array })
  data: TimelineData = [];

  @state()
  private openPanelId: string | null = null;

  /**
   * Allow multiple panels to be open at the same time
   */

  @property({ type: Boolean, attribute: true, reflect: true })
  allowMultipleOpen = false;

  /**
   * Different event for slot element click
   */

  @property({ type: Boolean, attribute: true, reflect: true })
  headerSlotEvent = false;

  private collapsibleController = new CollapsiblePanelController<GzCollapsiblePanelWebComponent>(
    this as LitElement & HTMLElement,
    this.allowMultipleOpen
  );

  static get styles() {
    return [unsafeCSS(`${componentStyles}`)];
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    if (_changedProperties.has('allowMultipleOpen')) {
      this.collapsibleController.setAllowMultipleOpen(this.allowMultipleOpen);
    }
  }

  private onTogglePanel(e: CustomEvent): void {
    const target = e.target as GzCollapsiblePanelWebComponent;
    this.collapsibleController.togglePanel(target);
    this.dispatchEvent(
      new CustomEvent(TimelineEvent.TOGGLE_PANEL, { detail: { target: target, isOpen: e.detail.isOpen } })
    );
  }

  private onSlotClick(e: CustomEvent): void {
    const target = e.target as GzCollapsiblePanelWebComponent;
    this.dispatchEvent(new CustomEvent(TimelineEvent.SLOT_HEADER_CLICK, { detail: { target: target } }));
  }

  private renderGroupedItems(group: TimelineGroup, isLastGroup = false): TemplateResult {
    return html`
      <gz-collapsible-panel
        @toggle="${this.onTogglePanel}"
        @slotClick="${this.onSlotClick}"
        class="group-panel"
        isOpen
        ?headerSlotEvent="${this.headerSlotEvent}"
      >
        <div slot="panel-header">
          <div class="header">
            <span class="name">${group.label}</span>
            <span class="count">(${group.items.length})</span>
          </div>
        </div>
        <gz-collapsible-timeline-group
          .items="${group.items}"
          .openPanelId="${this.openPanelId}"
          class="${isLastGroup ? 'last-group' : ''}"
          @toggleItemInGroup="${this.onToggleItemInGroup}"
        ></gz-collapsible-timeline-group>
      </gz-collapsible-panel>
    `;
  }

  private renderUngroupedItems(): TemplateResult {
    return html`
      <div class="ungrouped-timeline-items">
        <gz-collapsible-timeline-group
          .items="${this.data}"
          class="last-group"
          .openPanelId="${this.openPanelId}"
          @toggleItemInGroup="${this.onToggleItemInGroup}"
        ></gz-collapsible-timeline-group>
      </div>
    `;
  }
  private onToggleItemInGroup(event) {
    this.openPanelId = event.detail.isOpen ? event.detail.id : null;
    this.dispatchEvent(new CustomEvent(TimelineEvent.TOGGLE_ITEM_IN_GROUP, { detail: event.detail }));
  }

  render() {
    const isGrouped = Array.isArray(this.data) && this.data.length > 0 && 'items' in this.data[0];

    return isGrouped
      ? html`
          ${repeat(
            this.data as TimelineGroup[],
            group => group.label, // Unique key for each group
            (group: TimelineGroup, index: number) =>
              this.renderGroupedItems(group, index === (this.data as TimelineGroup[]).length - 1)
          )}
        `
      : this.renderUngroupedItems();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-collapsible-timeline': GzCollapsibleTimelineWebComponent;
  }
}
